/**
 * ---------------------------------------------------------------------------------------------------------------------
 * INSURED SHIPPING TOGGLE
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * Adds / removes the "insured shipping" product from the cart when the switch is toggled, and refreshes every cart
 * section (drawer, cart count, free shipping bar, ...) through the theme's own `cart:change` event, so nothing reloads.
 *
 * The element re-renders itself on every cart change (the drawer footer is replaced with fresh server markup), which
 * means the checked state is always derived from the cart and never kept in JavaScript.
 */

(() => {
  if (window.customElements.get('insured-shipping-toggle')) {
    return;
  }

  class InsuredShippingToggle extends HTMLElement {
    connectedCallback() {
      this.switchElement = this.querySelector('.insured-shipping__switch');
      this.statusElement = this.querySelector('[role="status"]');
      this.errorElement = this.querySelector('.insured-shipping__error');

      if (!this.switchElement) {
        return;
      }

      this._onSwitchChangedListener = this._onSwitchChanged.bind(this);
      this.switchElement.addEventListener('change', this._onSwitchChangedListener);

      // The cart would otherwise be left with insurance only, which cannot be checked out in a meaningful way.
      if (this.hasAttribute('data-remove-when-alone')) {
        this._remove().catch(() => document.dispatchEvent(new CustomEvent('cart:refresh')));
      }
    }

    disconnectedCallback() {
      this.switchElement?.removeEventListener('change', this._onSwitchChangedListener);
    }

    get variantId() {
      return parseInt(this.getAttribute('data-variant-id'), 10);
    }

    /**
     * The line item key is only known once the product sits in the cart. We fall back on the variant ID, which the Ajax
     * API also accepts, for the (unlikely) case where the markup was rendered before the line item existed.
     */
    get lineKey() {
      return this.getAttribute('data-line-key') || String(this.variantId);
    }

    async _onSwitchChanged() {
      if (this.hasAttribute('aria-busy') || !this.variantId) {
        return;
      }

      const shouldAdd = this.switchElement.checked;
      this.setAttribute('aria-busy', 'true');

      try {
        await (shouldAdd ? this._add() : this._remove());
      } catch (error) {
        // Roll the switch back and let the theme re-sync the drawer from the server.
        this.switchElement.checked = !shouldAdd;
        this.removeAttribute('aria-busy');
        this._showError(error);
        document.dispatchEvent(new CustomEvent('cart:refresh'));

        this.dispatchEvent(
          new CustomEvent('cart:error', { bubbles: true, detail: { error: error.message } })
        );

        return;
      }

      this._showError(null);

      // On success this element has already been replaced by fresh markup, but it may still be connected when the toggle
      // is rendered outside the cart drawer, so the busy state is always cleared.
      this.removeAttribute('aria-busy');
    }

    /**
     * Collects the section IDs the theme wants refreshed alongside the cart mutation (cart drawer, cart page, ...).
     */
    _bundledSections() {
      const sections = [];

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
      );

      return sections;
    }

    async _add() {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest' // Needed for Shopify to check inventory
        },
        body: JSON.stringify({
          items: [{ id: this.variantId, quantity: 1 }],
          sections: this._bundledSections()
        })
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw this._cartError('cart/add.js', this.variantId, response, responseJson);
      }

      // `cart/add.js` only returns the added lines, so the full cart is fetched to feed the theme's cart listeners.
      const cart = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
      cart['sections'] = responseJson['sections'];

      this._announce(true);
      this._dispatchCartChange(cart);
    }

    async _remove() {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: this.lineKey,
          quantity: 0,
          sections: this._bundledSections()
        })
      });

      const cart = await response.json();

      if (!response.ok) {
        throw this._cartError('cart/change.js', this.lineKey, response, cart);
      }

      this._announce(false);
      this._dispatchCartChange(cart);
    }

    /**
     * Shopify answers with `{ status, message, description }` on cart errors. That description is what distinguishes a
     * wrong ID from an out-of-stock variant, so it is both logged and carried on the error for the on-screen message.
     */
    _cartError(endpoint, id, response, payload) {
      const description = payload?.['description'] || payload?.['message'] || `HTTP ${response.status}`;

      console.error(
        `[insured-shipping] ${endpoint} refused id "${id}" (HTTP ${response.status}): ${description}`,
        payload
      );

      const error = new Error(description);
      error.detail = `${description} (id ${id}, HTTP ${response.status})`;

      return error;
    }

    _showError(error) {
      if (!this.errorElement) {
        return;
      }

      if (!error) {
        this.errorElement.hidden = true;
        this.errorElement.replaceChildren();
        return;
      }

      const message = window.themeVariables?.strings?.insuredShippingError || error.message;

      this.errorElement.hidden = false;
      this.errorElement.textContent = message;

      // Setup aid: surfaces Shopify's own wording so the cause is visible without opening the console. Delete this
      // block (and the `insured-shipping__error-detail` rule) once the product is configured correctly.
      if (error.detail) {
        const detail = document.createElement('span');
        detail.className = 'insured-shipping__error-detail';
        detail.textContent = error.detail;
        this.errorElement.append(detail);
      }
    }

    _announce(added) {
      if (!this.statusElement) {
        return;
      }

      const strings = window.themeVariables?.strings || {};
      this.statusElement.textContent = added ? strings.insuredShippingAdded || '' : strings.insuredShippingRemoved || '';
    }

    /**
     * `variant:add` as base event tells the cart drawer to swap its content immediately instead of waiting for the
     * 1.25s delay it uses for quantity edits.
     */
    _dispatchCartChange(cart) {
      // The theme's cart listeners read straight into `cart.sections[id]`, so a missing bundle would throw inside them.
      if (!cart['sections'] || Object.keys(cart['sections']).length === 0) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart }
        })
      );
    }
  }

  window.customElements.define('insured-shipping-toggle', InsuredShippingToggle);
})();
